import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Tag, Copy } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { api } from '@/shared/api/axios';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardBody } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Modal } from '@/shared/components/ui/Modal';
import { Badge } from '@/shared/components/ui/Badge';
import { Spinner } from '@/shared/components/ui/Spinner';
import { formatINR } from '@/shared/utils/currency';

interface Voucher {
  _id: string;
  code: string;
  name: string;
  type: 'fixed' | 'percent';
  value: number;
  maxDiscountMinor: number;
  validFrom: string | null;
  validTo: string | null;
  maxUsageTotal: number;
  currentUsageCount: number;
  maxUsagePerCustomer: number;
  minOrderValueMinor: number;
  isActive: boolean;
  createdAt: string;
}

function useVouchers() {
  return useQuery({
    queryKey: ['vouchers'],
    queryFn: async () => (await api.get<{ items: Voucher[] }>('/api/vouchers')).data.items,
  });
}

function useVoucherMutations() {
  const qc = useQueryClient();
  const create = useMutation({
    mutationFn: async (body: Record<string, unknown>) => (await api.post('/api/vouchers', body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vouchers'] }),
  });
  const update = useMutation({
    mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) => (await api.patch(`/api/vouchers/${id}`, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vouchers'] }),
  });
  return { create, update };
}

export function VouchersPage() {
  const { data: vouchers = [], isLoading } = useVouchers();
  const { create, update } = useVoucherMutations();
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', type: 'percent' as 'fixed' | 'percent', value: '', maxDiscountMinor: '', validFrom: '', validTo: '', maxUsageTotal: '', maxUsagePerCustomer: '1', minOrderValueMinor: '' });

  const handleCreate = () => {
    if (!form.code || !form.value) { toast.error('Code and value are required.'); return; }
    create.mutate({
      code: form.code, name: form.name, type: form.type,
      value: Number(form.value), maxDiscountMinor: Number(form.maxDiscountMinor) || 0,
      validFrom: form.validFrom || null, validTo: form.validTo || null,
      maxUsageTotal: Number(form.maxUsageTotal) || 0,
      maxUsagePerCustomer: Number(form.maxUsagePerCustomer) || 1,
      minOrderValueMinor: Number(form.minOrderValueMinor) || 0,
    }, {
      onSuccess: () => { toast.success('Voucher created.'); setShowCreate(false); setForm({ code: '', name: '', type: 'percent', value: '', maxDiscountMinor: '', validFrom: '', validTo: '', maxUsageTotal: '', maxUsagePerCustomer: '1', minOrderValueMinor: '' }); },
      onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed.'),
    });
  };

  const toggleActive = (v: Voucher) => {
    update.mutate({ id: v._id, isActive: !v.isActive }, { onSuccess: () => toast.success(v.isActive ? 'Deactivated.' : 'Activated.') });
  };

  const copyCode = (code: string) => { navigator.clipboard.writeText(code); toast.success('Code copied.'); };

  return (
    <div className="space-y-6 max-w-[1580px] mx-auto pb-16">
      <PageHeader icon={Tag} eyebrow="Marketing" title="Vouchers & Promo Codes" description={<>Create discount codes for distributors, campaigns, and loyalty rewards.</>} actions={<><Button onClick={() => setShowCreate(true)}><Plus className="h-4 w-4" /> New Voucher</Button></>} />

      {isLoading ? <div className="flex justify-center p-12"><Spinner /></div> : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {vouchers.map(v => (
            <Card key={v._id} className="relative">
              <CardBody className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold text-lg text-primary">{v.code}</span>
                    <button onClick={() => copyCode(v.code)} className="text-on-surface-variant hover:text-primary"><Copy className="h-3.5 w-3.5" /></button>
                  </div>
                  <Badge tone={v.isActive ? 'success' : 'neutral'}>{v.isActive ? 'Active' : 'Inactive'}</Badge>
                </div>
                {v.name && <p className="text-body-sm text-on-surface-variant">{v.name}</p>}
                <div className="text-2xl font-black text-on-surface">
                  {v.type === 'percent' ? `${v.value}% OFF` : `₹${v.value / 100} OFF`}
                </div>
                <div className="text-xs text-on-surface-variant space-y-1">
                  {v.maxUsageTotal > 0 && <p>Usage: {v.currentUsageCount}/{v.maxUsageTotal}</p>}
                  {v.validTo && <p>Expires: {new Date(v.validTo).toLocaleDateString()}</p>}
                  {v.minOrderValueMinor > 0 && <p>Min order: {formatINR(v.minOrderValueMinor / 100)}</p>}
                </div>
                <Button size="sm" variant={v.isActive ? 'outline' : 'primary'} onClick={() => toggleActive(v)} className="w-full">
                  {v.isActive ? 'Deactivate' : 'Activate'}
                </Button>
              </CardBody>
            </Card>
          ))}
          {vouchers.length === 0 && <p className="col-span-full text-center py-12 text-on-surface-variant">No vouchers yet. Create your first promo code.</p>}
        </div>
      )}

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Create Voucher" size="lg" footer={<><Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button><Button onClick={handleCreate} loading={create.isPending}>Create</Button></>}>
        <div className="grid gap-3 md:grid-cols-2">
          <Input label="Code *" value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="e.g. SUMMER20" />
          <Input label="Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Summer Sale 20%" />
          <Select label="Type" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value as 'fixed' | 'percent' }))} options={[{ value: 'percent', label: 'Percentage' }, { value: 'fixed', label: 'Fixed Amount (paise)' }]} />
          <Input label="Value *" type="number" value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))} placeholder={form.type === 'percent' ? 'e.g. 20' : 'e.g. 50000'} />
          {form.type === 'percent' && <Input label="Max Discount (paise)" type="number" value={form.maxDiscountMinor} onChange={e => setForm(f => ({ ...f, maxDiscountMinor: e.target.value }))} placeholder="0 = no cap" />}
          <Input label="Min Order Value (paise)" type="number" value={form.minOrderValueMinor} onChange={e => setForm(f => ({ ...f, minOrderValueMinor: e.target.value }))} placeholder="0 = no minimum" />
          <Input label="Valid From" type="date" value={form.validFrom} onChange={e => setForm(f => ({ ...f, validFrom: e.target.value }))} />
          <Input label="Valid To" type="date" value={form.validTo} onChange={e => setForm(f => ({ ...f, validTo: e.target.value }))} />
          <Input label="Max Usage Total" type="number" value={form.maxUsageTotal} onChange={e => setForm(f => ({ ...f, maxUsageTotal: e.target.value }))} placeholder="0 = unlimited" />
          <Input label="Max Per Customer" type="number" value={form.maxUsagePerCustomer} onChange={e => setForm(f => ({ ...f, maxUsagePerCustomer: e.target.value }))} placeholder="1" />
        </div>
      </Modal>
    </div>
  );
}
